import { useContext } from 'react';
import { CloudDownloadIcon } from 'assets/icons';
import { ButtonSize, ButtonStyle } from 'common/button';
import { NotificationType } from 'common/enums';
import { ModalWarning } from 'common/modal-warning';
import { appActions, AppContext } from 'context';
import { trackCustomEvent } from 'helpers/analytics';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';

type DownloadVaultModalProps = {
  vault: VaultType;
  onClose: () => void;
};

// Confirms an offline copy of the whole vault: the server builds a zip (every
// album, a page to browse it offline, private memories included) and emails a
// link to the requester.
const DownloadVaultModal = ({ vault, onClose }: DownloadVaultModalProps) => {
  const {
    state: {
      data: { user },
    },
    dispatch,
  } = useContext(AppContext);

  const notify = (title: string, type: NotificationType, message?: string) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title,
        type,
        message,
      }),
    });

  const requestDownload = async () => {
    try {
      await VaultsController.requestVaultDownload({
        id: vault.id,
        includePrivateClips: true,
      });
      trackCustomEvent('confirmRequestVaultDownload');
      onClose();
      notify('Download request submitted', NotificationType.Success);
    } catch {
      notify(
        'Failed to request download',
        NotificationType.Error,
        'Please try again',
      );
    }
  };

  return (
    <ModalWarning
      title="Download vault"
      icon={<CloudDownloadIcon />}
      iconVariant="bordered"
      content={
        <div>
          A zip file containing all content from <strong>{vault.title}</strong>{' '}
          will be sent to <strong>{user.email}</strong> within 24 hours.
        </div>
      }
      successButtonText="Request Download"
      successStyleButton={ButtonStyle.PrimaryFilled}
      successButtonSize={ButtonSize.Large}
      closeButtonText="Cancel"
      closeFn={onClose}
      successFn={requestDownload}
    />
  );
};

export { DownloadVaultModal };
